import {
  Badge,
  Button,
  Card,
  CommentBody,
  Facts,
  Loading,
  Modal,
  QuestionComments,
  formatDate,
} from "@corecrm/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { keys, useProjectQuestions, useQuestion } from "../api/queries";
import type { Question } from "../api/types";

/** One question, as a row: its title opens it. */
export function QuestionItem({
  question: q,
  onOpen,
  showProject = false,
}: {
  question: Question;
  onOpen: (q: Question) => void;
  showProject?: boolean;
}) {
  return (
    <div className="item">
      <div className="what">
        <button type="button" className="task-title" onClick={() => onOpen(q)}>
          <strong>{q.title}</strong>
        </button>
        <span>
          Asked by {q.asked_by} · {formatDate(q.created_at)}
          {q.task && ` · ${q.task.title}`}
          {showProject && (
            <>
              {" · "}
              <Link to={`/projects/${q.project.id}`}>{q.project.name}</Link>
            </>
          )}
        </span>
      </div>
      <Badge kind={q.status === "client" ? "acc" : "ok"}>
        {q.status === "client" ? "Waiting on you" : "Answered"}
      </Badge>
    </div>
  );
}

/** A question opened: what's asked, its answer and comments. Only a comment answers it (or
    reopens it): the comment box's checkbox. It won't close over a comment not yet posted. */
export function QuestionDialog({ question, onClose }: { question: Question; onClose: () => void }) {
  const live = useQuestion(question.id, question);
  const q = live.data ?? question;
  const qc = useQueryClient();
  const [drafting, setDrafting] = useState(false);
  const open = q.status === "client";
  const close = () => {
    if (!drafting) onClose();
  };
  return (
    <Modal
      title={q.title}
      onClose={close}
      footer={
        <>
          {drafting && <span className="faint">Post or clear your comment to close.</span>}
          <span style={{ flex: 1 }} />
          <Button variant="primary" disabled={drafting} onClick={close}>
            Close
          </Button>
        </>
      }
    >
      <div className="stack">
        <Facts
          items={[
            ["Project", <Link to={`/projects/${q.project.id}`}>{q.project.name}</Link>],
            ["Asked by", `${q.asked_by} · ${formatDate(q.created_at)}`],
            ...(q.task ? ([["Task", q.task.title]] as [string, string][]) : []),
            ["Status", open ? "Waiting on you" : `Answered ${formatDate(q.answered_at)}`],
          ]}
        />
        {q.description ? <p className="task-desc">{q.description}</p> : null}
        {q.answer && (
          <div className="q-answer" aria-label="Answer">
            <Badge kind="ok">Answer</Badge>{" "}
            <span className="faint">
              {q.answer.author} · {formatDate(q.answer.created_at)}
            </span>
            <CommentBody body={q.answer.body} />
          </div>
        )}
        <div>
          <h3 className="lib-h">Comments</h3>
          <QuestionComments
            api={api}
            questionId={q.id}
            projectId={q.project.id}
            status={q.status}
            onChanged={() => void qc.invalidateQueries({ queryKey: keys.questions })}
            onDraftChange={setDrafting}
          />
        </div>
      </div>
    </Modal>
  );
}

/** A question opened by its id (a comment thread's link). */
export function QuestionDialogById({ id, onClose }: { id: string; onClose: () => void }) {
  const q = useQuestion(id);
  if (q.isPending)
    return (
      <Modal title="Question" onClose={onClose}>
        <Loading />
      </Modal>
    );
  if (!q.data)
    return (
      <Modal title="Question" onClose={onClose} footer={<Button onClick={onClose}>Close</Button>}>
        <p className="muted">This question isn't open to you any more.</p>
      </Modal>
    );
  return <QuestionDialog question={q.data} onClose={onClose} />;
}

/** The project page's Questions card: the ones waiting on you, then those answered. */
export function QuestionsCard({ projectId }: { projectId: string }) {
  const questions = useProjectQuestions(projectId);
  const [opened, setOpened] = useState<Question | null>(null);
  if (questions.isPending || questions.error || !questions.data.length) return null;
  const waiting = questions.data.filter((q) => q.status === "client");
  const answered = questions.data.filter((q) => q.status !== "client");
  return (
    <Card
      title="Questions"
      action={waiting.length > 0 && <Badge kind="acc">{`${waiting.length} waiting on you`}</Badge>}
    >
      <div className="item-list" aria-label="Questions waiting on you">
        {waiting.map((q) => (
          <QuestionItem key={q.id} question={q} onOpen={setOpened} />
        ))}
      </div>
      {answered.length > 0 && (
        <>
          <h3 className="lib-h">Answered</h3>
          <div className="item-list" aria-label="Answered questions">
            {answered.map((q) => (
              <QuestionItem key={q.id} question={q} onOpen={setOpened} />
            ))}
          </div>
        </>
      )}
      {opened && <QuestionDialog question={opened} onClose={() => setOpened(null)} />}
    </Card>
  );
}
